import { createContext, useEffect, useState } from "react";
import type {
  IAdditionRequestContextData,
  IAdditionRequestProviderProps,
  IGetAvailableExamsResponse,
  IPages,
  IRequestAdditionFilterResponse,
} from "./types";
import { createAdditionRequestUtils } from "./utils";
import Loading from "@components/ui/Shared/Loading";
import NurseIcon from "@/assets/icons/NurseIcon";
import FileMedicalIcon from "@/assets/icons/FileMedicalIcon";
import ListIcon from "@/assets/icons/ListIcon";
import DoubleCheckBulletIcon from "@/assets/icons/DoubleCheckBulletIcon";

export const AdditionRequestContext =
  createContext<IAdditionRequestContextData | null>(null);

export const AdditionRequestProvider = ({
  children,
}: IAdditionRequestProviderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pages, setPages] = useState<IPages[]>([]);
  const [requestsMadeForPatient, setRequestsMadeForPatient] =
    useState<IRequestAdditionFilterResponse>();
  const [availableExams, setAvailableExams] =
    useState<IGetAvailableExamsResponse>();
  const [selectedExams, setSelectedExams] =
    useState<IGetAvailableExamsResponse>();
  const [files, setFiles] = useState<File[]>();
  const [newRequestId, setNewRequestId] = useState<number | null>(null);
  const [currentTablePage, setCurrentTablePage] = useState(0);
  const [_reasons, _setReasons] = useState<{ id: number; reason: string }[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      setPages([
        { number: 0, text: "Exame", icon: NurseIcon },
        { number: 1, text: "Arquivos", icon: FileMedicalIcon },
        { number: 2, text: "Conclusão", icon: DoubleCheckBulletIcon },
      ]);
      return;
    }



    if (requestsMadeForPatient?.additions.length === 0) {
      setPages([
        { number: 0, text: "Novos Exames", icon: NurseIcon },
        { number: 1, text: "Pedido Médico", icon: FileMedicalIcon },
        { number: 2, text: "Conclusão", icon: DoubleCheckBulletIcon },
      ]);
    }

    if ((requestsMadeForPatient?.additions.length ?? 0) > 0) {
      setPages([
        { number: 0, text: "Exames já Solicitados", icon: ListIcon },
        { number: 1, text: "Novos Exames", icon: NurseIcon },
        { number: 2, text: "Pedido Médico", icon: FileMedicalIcon },
        { number: 3, text: "Conclusão", icon: DoubleCheckBulletIcon },
      ]);
    }
  }, [requestsMadeForPatient, isEditMode]);

  const resetState = () => {
    setSelectedExams(undefined);
    setFiles(undefined);
    setNewRequestId(null);
    setIsEditMode(false);
  };

  const contextValue: IAdditionRequestContextData = {
    ...createAdditionRequestUtils({
      setRequestsMadeForPatient,
      setAvailableExams,
      selectedExams,
      setNewRequestId,
      setIsLoading,
      setFiles,
      setSelectedExams,
    }),
    requestsMadeForPatient,
    pages,
    availableExams,
    setAvailableExams,
    selectedExams,
    setSelectedExams,
    files,
    setFiles,
    newRequestId,
    currentTablePage,
    setCurrentTablePage,
    setRequestsMadeForPatient,
    resetState,
    isEditMode,
    setIsEditMode,
  };

  return (
    <AdditionRequestContext.Provider value={contextValue}>
      {isLoading && <Loading />}
      {children}
    </AdditionRequestContext.Provider>
  );
};
